import { forwardRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { useLanguage } from '../../context/LanguageContext';
import { fr, enGB, de, nl, es, it, pt, el } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';

// Register all locales
registerLocale('fr', fr);
registerLocale('en', enGB);
registerLocale('de', de);
registerLocale('nl', nl);
registerLocale('es', es);
registerLocale('it', it);
registerLocale('pt', pt);
registerLocale('el', el);

/**
 * DateTimePicker component with i18n support
 * Wraps react-datepicker with automatic locale detection
 *
 * @param {Object} props
 * @param {Date|string} props.value - Selected date value
 * @param {Function} props.onChange - Change handler (receives Date or ISO string based on returnString prop)
 * @param {boolean} props.showTimeSelect - Show time picker (default: false)
 * @param {boolean} props.dateOnly - Show only date picker (default: false)
 * @param {boolean} props.returnString - Return ISO string instead of Date object (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.required - Required field
 * @param {Date} props.minDate - Minimum selectable date
 * @param {Date} props.maxDate - Maximum selectable date
 * @param {Date} props.minTime - Minimum selectable time (for time filtering)
 * @param {Date} props.maxTime - Maximum selectable time (for time filtering)
 * @param {boolean} props.filterPassedTime - Auto-filter past times when minDate is today (default: false)
 */
const DateTimePicker = forwardRef(({
    value,
    onChange,
    showTimeSelect = true,
    dateOnly = false,
    returnString = true,
    className = '',
    placeholder,
    required = false,
    minDate,
    maxDate,
    minTime,
    maxTime,
    filterPassedTime = false,
    ...props
}, ref) => {
    const { language } = useLanguage();

    // Convert string value to Date if needed
    const dateValue = value ? (typeof value === 'string' ? new Date(value) : value) : null;

    // Calculate dynamic minTime/maxTime based on selected date
    const getFilteredTime = () => {
        // If explicit minTime/maxTime provided, use them
        if (minTime || maxTime) {
            return { minTime, maxTime };
        }

        // If filterPassedTime is enabled and we have a minDate
        if (filterPassedTime && minDate) {
            const now = new Date();
            const minDateObj = minDate instanceof Date ? minDate : new Date(minDate);
            const selectedDate = dateValue || now;

            // Check if selected date is the same day as minDate
            const isSameDay = selectedDate.toDateString() === minDateObj.toDateString();

            if (isSameDay) {
                // Filter times before the minDate time
                const filterMinTime = new Date(selectedDate);
                filterMinTime.setHours(minDateObj.getHours(), minDateObj.getMinutes(), 0, 0);

                const filterMaxTime = new Date(selectedDate);
                filterMaxTime.setHours(23, 45, 0, 0);

                return { minTime: filterMinTime, maxTime: filterMaxTime };
            }
        }

        return { minTime: undefined, maxTime: undefined };
    };

    const { minTime: computedMinTime, maxTime: computedMaxTime } = getFilteredTime();

    // Handle change - convert to ISO string if returnString is true
    const handleChange = (date) => {
        if (!date) {
            onChange(null);
            return;
        }

        if (returnString) {
            if (dateOnly) {
                // Return YYYY-MM-DD format for date only
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                onChange(`${year}-${month}-${day}`);
            } else {
                // Return ISO string format compatible with datetime-local input
                // Format: YYYY-MM-DDTHH:mm
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                onChange(`${year}-${month}-${day}T${hours}:${minutes}`);
            }
        } else {
            onChange(date);
        }
    };

    // Get date format based on locale
    const getDateFormat = () => {
        if (dateOnly) {
            switch (language) {
                case 'en':
                    return 'MM/dd/yyyy';
                case 'de':
                case 'nl':
                    return 'dd.MM.yyyy';
                default:
                    return 'dd/MM/yyyy';
            }
        }

        // DateTime format
        switch (language) {
            case 'en':
                return 'MM/dd/yyyy HH:mm';
            case 'de':
            case 'nl':
                return 'dd.MM.yyyy HH:mm';
            default:
                return 'dd/MM/yyyy HH:mm';
        }
    };

    // Get time format (24h for most European countries)
    const getTimeFormat = () => {
        return language === 'en' ? 'h:mm aa' : 'HH:mm';
    };

    // Base classes matching the existing input styles
    const baseClasses = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-cabaza-purple focus:border-transparent';

    return (
        <DatePicker
            ref={ref}
            selected={dateValue}
            onChange={handleChange}
            locale={language}
            dateFormat={getDateFormat()}
            timeFormat={getTimeFormat()}
            showTimeSelect={showTimeSelect && !dateOnly}
            timeIntervals={15}
            timeCaption={language === 'fr' ? 'Heure' : language === 'de' ? 'Zeit' : language === 'nl' ? 'Tijd' : language === 'es' ? 'Hora' : language === 'it' ? 'Ora' : language === 'pt' ? 'Hora' : language === 'el' ? 'Ώρα' : 'Time'}
            className={`${baseClasses} ${className}`}
            wrapperClassName="w-full"
            popperClassName="datepicker-popper-high-z"
            placeholderText={placeholder}
            required={required}
            minDate={minDate}
            maxDate={maxDate}
            minTime={computedMinTime}
            maxTime={computedMaxTime}
            autoComplete="off"
            shouldCloseOnSelect={true}
            withPortal={false}
            portalId="datepicker-portal"
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            yearDropdownItemNumber={100}
            scrollableYearDropdown
            {...props}
        />
    );
});

DateTimePicker.displayName = 'DateTimePicker';

export default DateTimePicker;
